<template>
  <BKInput
    v-model="internalValue"
    :label="label"
    type="number"
    :prefix="prefix"
    :min="0"
    :error="error"
    :error-messages="errorMessages"
    :placeholder="placeholder"
    :readonly="readonly"
    :required="required"
    :rules="rules"
    :disabled="disabled"
    :dense="dense"
    :hide-details="hideDetails"
    :color="color"
    v-bind="$attrs"
    @input="handleInput"
    @blur="emit('blur')"
    @focus="emit('focus')"
    @keyup="emit('keyup', $event)"
  />
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import BKInput from './BKInput.vue'

const emit = defineEmits<{
  (e: 'blur'): void
  (e: 'focus'): void
  (e: 'input', value: number): void
  (e: 'keyup', value: KeyboardEvent): void
  (e: 'update:modelValue', value: number): void
}>()

const props = withDefaults(
  defineProps<{
    value: number // Value in cents
    label?: string
    error?: boolean
    errorMessages?: string | string[]
    placeholder?: string
    readonly?: boolean
    required?: boolean
    rules?: ((value: string | number | undefined) => unknown)[]
    disabled?: boolean
    dense?: boolean
    hideDetails?: boolean | 'auto'
    color?: string
    prefix?: string
  }>(),
  {
    value: 0,
    label: '',
    error: false,
    errorMessages: () => [],
    placeholder: '',
    readonly: false,
    required: false,
    rules: () => [],
    disabled: false,
    dense: false,
    hideDetails: 'auto',
    color: 'var(--color-blue-800)',
    prefix: '$',
  }
)

// Internal value initialized only on mount
const internalValue = ref('')

// Initialize internal value only on mount
onMounted(() => {
  internalValue.value = (props.value / 100).toFixed(2)
})

function handleInput(value: string | number) {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      const centsValue = 0
      emit('input', centsValue)
      emit('update:modelValue', centsValue)
      return
    }

    const numericValue = parseFloat(value) * 100

    if (Number.isNaN(numericValue)) {
      const centsValue = 0
      emit('input', centsValue)
      emit('update:modelValue', centsValue)
      return
    }

    const centsValue = Math.max(0, Math.round(numericValue))
    emit('input', centsValue)
    emit('update:modelValue', centsValue)
  } else {
    const centsValue = Math.max(0, Math.round(value * 100 || 0))
    emit('input', centsValue)
    emit('update:modelValue', centsValue)
  }
}
</script>
